import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import KeywordTimeline from "@/components/KeywordTimeline";
import EducationalBlock from "@/components/EducationalBlock";
import SavedViewsBar from "@/components/SavedViewsBar";
// eslint-disable-next-line no-unused-vars
import ProjectAiSummary from "@/components/ProjectAiSummary";
import { ArrowLeft, ArrowUpRight, ArrowDownRight, Minus, Upload, Download, Plus, Search, RefreshCw, X, Sparkles, ExternalLink, TrendingUp, ChevronDown, Users, Share2, Copy, Trash2, Target, Clock, FileText, Zap, CheckSquare, Square, Pause, Play, Tag, Filter as FilterIcon, MoreVertical, Archive, ArchiveRestore, MessageSquare, Columns3, StickyNote, MousePointerClick, Languages, Lock, AlertTriangle, Check } from "lucide-react";
import { LineChart, Line, YAxis, XAxis, ResponsiveContainer, Tooltip as RTooltip, ComposedChart, LabelList } from "recharts";
import { ACTIVITY_CATEGORIES, categoryFor, colorFor, categoryLabelFor } from "@/lib/activityCategory";
import SearchableSelect from "@/components/SearchableSelect";
import SchedulePanel from "@/components/SchedulePanel";
import ActivityTimeline from "@/components/ActivityTimeline";
import ProReportModal from "@/components/ProReportModal";
import ExportModal from "@/components/ExportModal";
import { loadCountries, loadLanguages, countryName, languageName } from "@/lib/meta";

const MOVEMENT_OPTIONS = [
  { v: "", label: "Any movement" },
  { v: "up", label: "Moved up" },
  { v: "down", label: "Moved down" },
  { v: "entered_top3", label: "Entered top 3" },
  { v: "entered_top10", label: "Entered top 10" },
  { v: "dropped_out_top10", label: "Dropped out of top 10" },
  { v: "not_in_top100", label: "Not in top 100" },
  { v: "no_change", label: "No change" },
];

export default function ProjectDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  // When the account's trial has ended, cap the daily-rank strip at the
  // trial expiry date. Post-expiry days (never tracked, never will be)
  // are hidden entirely — matches the server-side gate in
  // `_run_due_schedules` which skips scheduled refreshes for expired trials.
  const trialCapIso = useMemo(() => {
    if (!user?.trial_expired) return null;
    const raw = user?.trial_ends_at;
    if (!raw) return null;
    try {
      // trial_ends_at is an ISO datetime — grab the YYYY-MM-DD.
      return String(raw).slice(0, 10);
    } catch { return null; }
  }, [user?.trial_expired, user?.trial_ends_at]);
  const [project, setProject] = useState(null);
  const [keywords, setKeywords] = useState([]);
  const [filters, setFilters] = useState({ q: "", country: "", language: "", device: "", engine: "", movement: "", tag: "", status: "", min_position: "", max_position: "", min_volume: "", min_opportunity: "", competitor_overtake: "" });
  const [sort, setSort] = useState({ field: "current_rank", order: 1 });
  const [showAdd, setShowAdd] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [drawer, setDrawer] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [competitors, setCompetitors] = useState([]);
  const [showComp, setShowComp] = useState(false);
  const [showProReport, setShowProReport] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [focus, setFocus] = useState("all");
  const [openMenuId, setOpenMenuId] = useState(null);
  const [openMenuCoords, setOpenMenuCoords] = useState(null);
  const [showColMenu, setShowColMenu] = useState(false);
  const DEFAULT_COLS = { country: true, language: false, device: true, rank: true, prev: true, change: true, volume: true, opp: true, ranking_url: true, trend: true, updated: true };
  const [cols, setCols] = useState(() => {
    try { return { ...DEFAULT_COLS, ...(JSON.parse(localStorage.getItem("sn_kw_cols") || "{}")) }; } catch { return DEFAULT_COLS; }
  });
  useEffect(() => { localStorage.setItem("sn_kw_cols", JSON.stringify(cols)); }, [cols]);
  const [countryList, setCountryList] = useState({ items: [], common: [] });
  const [languageList, setLanguageList] = useState({ items: [], common: [] });
  useEffect(() => { loadCountries().then(setCountryList); loadLanguages().then(setLanguageList); }, []);
  const [tab, setTab] = useState("keywords");
  const [usage, setUsage] = useState(null);

  const loadUsage = () => api.get("/dashboard/usage").then((r) => setUsage(r.data)).catch(() => {});
  useEffect(() => { loadUsage(); }, []);

  const loadProject = () => api.get(`/projects/${id}`).then((r) => setProject(r.data));
  const loadCompetitors = () => api.get("/competitors", { params: { project_id: id } }).then((r) => setCompetitors(r.data));
  const loadKeywords = () => {
    const params = { project_id: id, include_archived: true };
    Object.entries(filters).forEach(([k, v]) => { if (v !== "" && v !== null) params[k] = v; });
    params.sort = sort.field;
    params.order = sort.order;
    return api.get("/keywords", { params }).then((r) => setKeywords(r.data));
  };
  useEffect(() => { loadProject(); loadCompetitors(); }, [id]); // eslint-disable-line
  useEffect(() => { loadKeywords(); }, [id, sort]); // eslint-disable-line
  useEffect(() => {
    const t = setTimeout(loadKeywords, 250);
    return () => clearTimeout(t);
  }, [filters]); // eslint-disable-line

  const clearFilters = () => setFilters({ q: "", country: "", language: "", device: "", engine: "", movement: "", tag: "", status: "", min_position: "", max_position: "", min_volume: "", min_opportunity: "", competitor_overtake: "" });
  const activeFilterCount = Object.values(filters).filter((v) => v !== "").length;

  const toggleSort = (field) => setSort((s) => s.field === field ? { field, order: -s.order } : { field, order: 1 });

  const refresh = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/keywords/${id}/refresh`);
      const mode = r?.data?.mode;
      const queued = r?.data?.queued ?? 0;
      const skippedToday = r?.data?.skipped_today ?? 0;
      const skippedCap = r?.data?.skipped_cap ?? 0;
      await loadProject(); await loadKeywords(); loadUsage();
      if (queued > 0) {
        toast.success(`Refresh queued for ${queued} keyword${queued === 1 ? "" : "s"} — real ranks land in ~1 min. This page auto-refreshes.`);
        // Poll every 20s up to 3 min so pending rows fill without a manual reload.
        let ticks = 0;
        const pollId = setInterval(async () => {
          ticks += 1;
          try {
            await loadKeywords(); loadUsage();
            const pending = (document.querySelectorAll('[data-testid^="kw-checking-"]') || []).length;
            if (pending === 0 || ticks >= 9) clearInterval(pollId);
          } catch { clearInterval(pollId); }
        }, 20000);
      } else if (skippedCap > 0) {
        toast.info(`Daily check limit reached (${skippedCap} keyword${skippedCap === 1 ? "" : "s"} skipped). Resets at 00:00 account time.`);
      } else if (skippedToday > 0) {
        toast.info("Already updated today. Your next ranking update is available tomorrow.");
      } else if (mode === "no-op") {
        toast.info("Nothing to refresh — no active keywords in this project.");
      } else {
        toast.success("Rankings refreshed.");
      }
    } catch { toast.error("Refresh failed."); }
    finally { setBusy(false); }
  };

  const openKeyword = async (k) => {
    setDrawer(k);
    setAnalysis(k.last_ai_analysis || null);
    setComparison(null);
    if (competitors.length > 0) {
      try {
        const r = await api.get("/competitors/compare", { params: { keyword_id: k.id } });
        setComparison(r.data);
      } catch (e) { console.debug("[ProjectDetail] competitor compare failed:", e); }
    }
  };

  const runAi = async () => {
    if (!drawer) return;
    setAiBusy(true);
    try {
      const r = await api.post("/ai/analyze-keyword", { keyword_id: drawer.id });
      setAnalysis(r.data);
      toast.success("AI analysis complete.");
    } catch { toast.error("AI analysis failed."); }
    finally { setAiBusy(false); }
  };

  const canEditActivities = ["owner", "admin", "editor"].includes(project?.role || "owner");

  const buildShareLink = (token, customDomain) => {
    if (customDomain) {
      const host = customDomain.replace(/^https?:\/\//, "").replace(/\/$/, "");
      return `https://${host}/share/${token}`;
    }
    return `${window.location.origin}/share/${token}`;
  };

  const shareProject = async () => {
    try {
      const r = await api.post("/share", { kind: "project", target_id: id, expires_days: 30 });
      const link = buildShareLink(r.data.token, r.data.custom_share_domain);
      await navigator.clipboard.writeText(link);
      toast.success(r.data.custom_share_domain ? `Share link copied — served from ${r.data.custom_share_domain}.` : "Share link copied to clipboard (valid 30 days).");
    } catch { toast.error("Failed to create share link."); }
  };

  const shareReport = async () => {
    try {
      const r = await api.post("/share", { kind: "report", target_id: id, expires_days: 30 });
      const link = buildShareLink(r.data.token, r.data.custom_share_domain);
      await navigator.clipboard.writeText(link);
      toast.success(r.data.custom_share_domain ? `Branded report link copied — served from ${r.data.custom_share_domain}.` : "Branded client report link copied to clipboard.");
    } catch { toast.error("Failed to create report link."); }
  };

  const enrichVolumes = async () => {
    try {
      toast.info("Enriching search volumes…");
      const r = await api.post(`/enrichment/project/${id}/run`, null, { params: { force: false } });
      await loadKeywords();
      toast.success(`Updated ${r.data.updated} · skipped ${r.data.skipped_manual} manual · ${r.data.skipped_fresh} still fresh`);
    } catch { toast.error("Enrichment failed."); }
  };

  // ---------- Focus mode + selection ----------
  const visibleKeywords = useMemo(() => {
    // "Paused" and "Archived" focus tabs are the only two views that surface
    // their respective statuses. Every other tab hides archived by default —
    // paused stays visible in "All" so users can act on it without a filter.
    if (focus === "paused") return keywords.filter((k) => k.status === "paused");
    if (focus === "archived") return keywords.filter((k) => k.status === "archived");
    let base = keywords.filter((k) => k.status !== "archived");
    if (focus === "selected") return base.filter((k) => selected.has(k.id));
    if (focus === "no-paused") return base.filter((k) => k.status !== "paused");
    if (focus === "active-only") return base.filter((k) => (k.status || "active") === "active");
    if (focus === "no-unchanged") return base.filter((k) => {
      const c = k.current_rank, p = k.previous_rank;
      return c !== p;
    });
    if (focus === "improved") return base.filter((k) => {
      const c = k.current_rank, p = k.previous_rank;
      if (p == null || c == null) return c != null && p == null;
      return c < p;
    });
    if (focus === "dropped") return base.filter((k) => {
      const c = k.current_rank, p = k.previous_rank;
      if (p == null && c == null) return false;
      if (c == null) return true;
      if (p == null) return false;
      return c > p;
    });
    if (focus === "top3") return base.filter((k) => {
      const c = k.current_rank, p = k.previous_rank;
      // Guard against ranks > 100 (or null "not ranking" state) sneaking in.
      return c != null && c >= 1 && c <= 3 && (p == null || p > 3);
    });
    if (focus === "top10") return base.filter((k) => {
      // "Entered top 10" now matches any keyword currently ranking 10 or
      // better — regardless of where it was previously. This surfaces every
      // top-10 keyword in one glance, not only fresh entrants.
      const c = k.current_rank;
      return c != null && c >= 1 && c <= 10;
    });
    if (focus === "lost-top10") return base.filter((k) => {
      const c = k.current_rank, p = k.previous_rank;
      return p != null && p <= 10 && (c == null || c > 10);
    });
    if (focus === "not-top100") return base.filter((k) => {
      // Keyword has been checked at least once (has history or a prior rank)
      // and is currently not ranking in the top 100.
      const c = k.current_rank, p = k.previous_rank;
      const hasBeenChecked = (k.history && k.history.length > 0) || p != null || c != null;
      return hasBeenChecked && (c == null || c > 100);
    });
    if (focus === "overtaken") return base.filter((k) => !!k.competitor_overtake);
    if (focus === "delayed") return base.filter((k) => ["delayed", "error", "no_result"].includes(k.status));
    return base;
  }, [keywords, focus, selected]);

  const allVisibleSelected = visibleKeywords.length > 0 && visibleKeywords.every((k) => selected.has(k.id));
  const someVisibleSelected = visibleKeywords.some((k) => selected.has(k.id));

  const selectionStats = useMemo(() => {
    if (selected.size === 0) return null;
    const rows = keywords.filter((k) => selected.has(k.id));
    let vol = 0, ranks = [], improved = 0, dropped = 0, netGain = 0;
    rows.forEach((k) => {
      vol += k.search_volume || 0;
      if (typeof k.current_rank === "number") ranks.push(k.current_rank);
      netGain += k.estimated_click_gain || 0;
      const c = k.current_rank, p = k.previous_rank;
      if (c != null && p != null) {
        if (c < p) improved += 1;
        else if (c > p) dropped += 1;
      } else if (c != null && p == null) improved += 1;
      else if (c == null && p != null) dropped += 1;
    });
    const avgRank = ranks.length ? Math.round(ranks.reduce((a, b) => a + b, 0) / ranks.length) : null;
    return { count: rows.length, volume: vol, avgRank, improved, dropped, netGain };
  }, [keywords, selected]);

  const toggleSelect = (kid) => setSelected((s) => {
    const n = new Set(s);
    if (n.has(kid)) n.delete(kid); else n.add(kid);
    return n;
  });
  const selectAllVisible = () => setSelected((s) => {
    const n = new Set(s);
    if (allVisibleSelected) visibleKeywords.forEach((k) => n.delete(k.id));
    else visibleKeywords.forEach((k) => n.add(k.id));
    return n;
  });
  const clearSelection = () => setSelected(new Set());

  const runBulk = async (action, extra = {}, ids = null) => {
    const list = ids || Array.from(selected);
    if (list.length === 0) return;
    try {
      const r = await api.post("/keywords/bulk-action", { action, keyword_ids: list, ...extra });
      const label = { add_tag: "Tag added to", remove_tag: "Tag removed from", pause: "Paused", resume: "Resumed", archive: "Archived", restore: "Restored", delete: "Deleted", refresh: "Refreshed" }[action] || "Updated";
      toast.success(`${label} ${list.length} keyword${list.length === 1 ? '' : 's'}.`);
      if (!ids) clearSelection();
      loadKeywords();
      return r.data;
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk action failed."); }
  };

  const shareSelected = async () => {
    if (selected.size === 0) return;
    const ids = Array.from(selected);
    try {
      const r = await api.post("/share", { kind: "project", target_id: id, expires_days: 30, keyword_ids: ids });
      const link = r.data.custom_share_domain
        ? `https://${r.data.custom_share_domain.replace(/^https?:\/\//, "").replace(/\/$/, "")}/share/${r.data.token}`
        : `${window.location.origin}/share/${r.data.token}`;
      await navigator.clipboard.writeText(link);
      toast.success(`Share link for ${ids.length} keyword${ids.length === 1 ? '' : 's'} copied.`);
    } catch { toast.error("Failed to share selected."); }
  };

  const addSelectedToReport = async () => {
    if (selected.size === 0) return;
    try {
      const r = await api.post("/share", { kind: "report", target_id: id, expires_days: 30, keyword_ids: Array.from(selected) });
      const link = r.data.custom_share_domain
        ? `https://${r.data.custom_share_domain.replace(/^https?:\/\//, "").replace(/\/$/, "")}/share/${r.data.token}`
        : `${window.location.origin}/share/${r.data.token}`;
      await navigator.clipboard.writeText(link);
      toast.success("Client report link for selected keywords copied.");
    } catch { toast.error("Failed to build report."); }
  };

  const exportSelectedCsv = () => {
    // Client-side CSV of currently selected rows using existing data
    const rows = keywords.filter((k) => selected.has(k.id));
    if (rows.length === 0) return;
    // Format ISO timestamps in the user's local timezone with an unambiguous
    // offset label (e.g. "2026-08-07 15:14 (UTC+08:00)") so any reader knows
    // exactly which wall-clock the timestamp refers to.
    const tzName = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    const fmtDt = (iso) => {
      if (!iso) return "";
      try {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return iso;
        const parts = new Intl.DateTimeFormat('sv-SE', {
          timeZone: tzName,
          year: 'numeric', month: '2-digit', day: '2-digit',
          hour: '2-digit', minute: '2-digit', hour12: false,
        }).formatToParts(d).reduce((acc, p) => { acc[p.type] = p.value; return acc; }, {});
        // Compute the current offset for this zone at that instant.
        const offsetMin = -new Date(d.toLocaleString('en-US', { timeZone: 'UTC' })).getTime() / 60000
          + new Date(d.toLocaleString('en-US', { timeZone: tzName })).getTime() / 60000;
        const sign = offsetMin >= 0 ? '+' : '-';
        const oh = String(Math.floor(Math.abs(offsetMin) / 60)).padStart(2, '0');
        const om = String(Math.abs(offsetMin) % 60).padStart(2, '0');
        return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute} (UTC${sign}${oh}:${om})`;
      } catch { return iso; }
    };
    const preamble = `# Exported ${fmtDt(new Date().toISOString())} — 'last_updated_at' shown in ${tzName}`;
    const header = ["keyword","country","device","engine","current_rank","previous_rank","search_volume","status","tags","last_updated_at"];
    const csv = [preamble, header.join(",")].concat(rows.map((k) => [
      `"${(k.keyword || "").replace(/"/g,'""')}"`,
      k.country || "", k.device || "", k.engine || "",
      k.current_rank ?? "", k.previous_rank ?? "",
      k.search_volume ?? "", k.status || "active",
      `"${(k.tags || []).join('|')}"`,
      `"${fmtDt(k.last_updated_at)}"`,
    ].join(","))).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `serpnex-${(project?.domain || 'export').replace(/\./g,'-')}-selected.csv`;
    a.click();
  };

  return (
    <div className={`p-8 lg:p-10 ${selected.size > 0 ? 'lg:pb-[180px] pb-[180px]' : ''}`}>
      <div className="flex items-center gap-2 text-xs text-[#8A94A6]">
        <Link to="/app/projects" className="hover:text-[#0047FF]">Projects</Link>
        <span>·</span>
        <span className="text-[#0A0D14]">{project?.name || '…'}</span>
      </div>

      <header className="mt-3 flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-display font-bold text-[#0A0D14]">{project?.name}</h1>
          <div className="mt-1 flex items-center gap-3 text-sm text-[#525B6A]">
            <span>{project?.domain}</span>
            <span>·</span>
            <span className="tabular-nums">{keywords.length} keywords</span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Competitors + Enrich volumes temporarily hidden per product request */}
          {/* <button onClick={() => setShowComp(true)} data-testid="pd-competitors" className="btn-ghost px-3 py-2 rounded-md text-sm inline-flex items-center gap-1"><Users className="h-3.5 w-3.5" /> Competitors <span className="tabular-nums text-[#0047FF] font-medium">{competitors.length}</span></button> */}
          <button onClick={shareProject} data-testid="pd-share" className="btn-ghost px-3 py-2 rounded-md text-sm inline-flex items-center gap-1"><Share2 className="h-3.5 w-3.5" /> Share</button>
          {/* Client report temporarily hidden per product request */}
          {/* <button onClick={shareReport} data-testid="pd-share-report" className="btn-ghost px-3 py-2 rounded-md text-sm inline-flex items-center gap-1"><FileText className="h-3.5 w-3.5" /> Client report</button> */}
          <button onClick={() => setShowProReport(true)} data-testid="pd-pro-report" className="btn-ghost px-3 py-2 rounded-md text-sm inline-flex items-center gap-1"><FileText className="h-3.5 w-3.5" /> Pro report</button>
          {/* <button onClick={enrichVolumes} data-testid="pd-enrich" title="Estimate search volumes from API" className="btn-ghost px-3 py-2 rounded-md text-sm inline-flex items-center gap-1"><Zap className="h-3.5 w-3.5" /> Enrich volumes</button> */}
          <button onClick={() => setShowExport(true)} data-testid="pd-export" className="btn-ghost px-3 py-2 rounded-md text-sm inline-flex items-center gap-1"><Download className="h-3.5 w-3.5" /> Export</button>
          <button onClick={() => setShowImport(true)} data-testid="pd-import-open" className="btn-ghost px-3 py-2 rounded-md text-sm inline-flex items-center gap-1"><Upload className="h-3.5 w-3.5" /> Import keywords</button>
          <button onClick={() => setShowAdd(true)} data-testid="pd-add-keywords" className="btn-primary px-3 py-2 rounded-md text-sm inline-flex items-center gap-1"><Plus className="h-3.5 w-3.5" /> Add keywords</button>
        </div>
      </header>

      {/* Tabs */}
      <div className="mt-6 border-b border-[#E2E8F0] flex items-center gap-1" data-testid="project-tabs">
        <button
          data-testid="tab-keywords"
          onClick={() => setTab("keywords")}
          className={`px-4 py-2 -mb-px text-sm font-medium border-b-2 transition ${tab === "keywords" ? "border-[#0047FF] text-[#0047FF]" : "border-transparent text-[#525B6A] hover:text-[#0A0D14]"}`}
        >
          Keywords <span className="ml-1 tabular-nums text-xs text-[#8A94A6]">{keywords.length}</span>
        </button>
        <button
          data-testid="tab-activities"
          onClick={() => setTab("activities")}
          className={`px-4 py-2 -mb-px text-sm font-medium border-b-2 transition ${tab === "activities" ? "border-[#0047FF] text-[#0047FF]" : "border-transparent text-[#525B6A] hover:text-[#0A0D14]"}`}
        >
          SEO Activity Timeline
        </button>
      </div>

      {tab === "activities" ? (
        <ActivityTimeline projectId={id} project={project} keywords={keywords} canEdit={canEditActivities} />
      ) : (
      <>
      {/* Saved views + AI summary — Saved views temporarily hidden per product request */}
      {/*
      <div className="mt-4">
        <SavedViewsBar projectId={id} filters={filters} onApply={(f) => setFilters({ q: "", country: "", device: "", engine: "", movement: "", tag: "", status: "", min_position: "", max_position: "", min_volume: "", min_opportunity: "", competitor_overtake: "", ...f })} />
      </div>
      */}

      <div className="mt-6">
        {/* Temporarily hidden — Project narrative is being redesigned. */}
        {/* <ProjectAiSummary projectId={id} /> */}
      </div>

      {/* Plan status row: Refresh schedule + Keywords stored + Today's
          ranking updates. Stacks on mobile, three columns on lg+. Refresh
          schedule spans 2 columns so its inline day/time details have room
          to breathe. */}
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-4 gap-4">
        <SchedulePanel
          projectId={id}
          canEdit={(project?.role || "viewer") === "owner" || (project?.role || "viewer") === "admin"}
          className="card-crisp p-4 lg:col-span-2"
        />
        {usage ? (
          <>
            <div className="card-crisp p-4" data-testid="plan-usage-widget">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-medium uppercase tracking-wider text-[#8A94A6]">Keywords stored</span>
                <span className="text-[11px] text-[#64748B] capitalize">{usage.plan} plan</span>
              </div>
              <div className="mt-2 flex items-baseline gap-2">
                <span className="text-2xl font-semibold text-[#0A0D14] tabular-nums" data-testid="usage-keywords-total">{usage.keywords.total}</span>
                <span className="text-sm text-[#64748B]">/ {usage.keywords.cap}</span>
              </div>
              <div className="mt-1 flex gap-3 text-[11px] text-[#64748B]">
                <span>Active <span className="font-medium text-[#0A0D14]">{usage.keywords.by_status.active}</span></span>
                <span>Paused <span className="font-medium text-[#0A0D14]">{usage.keywords.by_status.paused}</span></span>
                <span>Archived <span className="font-medium text-[#0A0D14]">{usage.keywords.by_status.archived}</span></span>
              </div>
              <div className="mt-2 h-1.5 w-full rounded-full bg-[#E2E8F0] overflow-hidden">
                <div className="h-full bg-[#0047FF] transition-all" style={{ width: `${Math.min(100, (usage.keywords.total / Math.max(1, usage.keywords.cap)) * 100)}%` }} />
              </div>
            </div>
            <div className="card-crisp p-4">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-medium uppercase tracking-wider text-[#8A94A6]">Today's ranking updates</span>
                <span className="text-[11px] text-[#64748B]">Resets 00:00</span>
              </div>
              <div className="mt-2 flex items-baseline gap-2">
                <span className="text-2xl font-semibold text-[#0A0D14] tabular-nums" data-testid="usage-checks-used">{usage.daily_checks.used}</span>
                <span className="text-sm text-[#64748B]">/ {usage.daily_checks.cap}</span>
              </div>
              <div className="mt-1 text-[11px] text-[#64748B]">{usage.daily_checks.remaining} remaining</div>
              <div className="mt-2 h-1.5 w-full rounded-full bg-[#E2E8F0] overflow-hidden">
                <div className="h-full bg-emerald-500 transition-all" style={{ width: `${Math.min(100, (usage.daily_checks.used / Math.max(1, usage.daily_checks.cap)) * 100)}%` }} />
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="card-crisp p-4" />
            <div className="card-crisp p-4" />
          </>
        )}
      </div>

      {/* Filters bar */}
      <div className="mt-6 card-crisp p-4">
        <div className="grid md:grid-cols-6 gap-3">
          <div className="md:col-span-2 relative">
            <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[#8A94A6]" />
            <input placeholder="Search keywords…" data-testid="filter-search" className="w-full pl-9 pr-3 py-2 border border-[#E2E8F0] rounded-md text-sm" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} />
          </div>
          <SearchableSelect items={countryList.items} common={countryList.common} value={filters.country} onChange={(v) => setFilters({ ...filters, country: v })} testid="filter-country" emptyLabel="All countries" allowClear className="min-w-[180px]" />
          <SearchableSelect items={languageList.items} common={languageList.common} value={filters.language} onChange={(v) => setFilters({ ...filters, language: v })} testid="filter-language" emptyLabel="All languages" allowClear className="min-w-[160px]" />
          <select data-testid="filter-device" value={filters.device} onChange={(e) => setFilters({ ...filters, device: e.target.value })} className="px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white">
            <option value="">All devices</option><option>desktop</option><option>mobile</option>
          </select>
          <select data-testid="filter-engine" value={filters.engine} onChange={(e) => setFilters({ ...filters, engine: e.target.value })} className="px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white">
            <option value="">All engines</option><option>google</option><option>bing</option>
          </select>
          <select data-testid="filter-movement" value={filters.movement} onChange={(e) => setFilters({ ...filters, movement: e.target.value })} className="px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white">
            {MOVEMENT_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
          </select>
          {/* Temporarily hidden — "Any overtake state" filter is being redesigned. */}
          {/*
          <select data-testid="filter-overtake" value={filters.competitor_overtake} onChange={(e) => setFilters({ ...filters, competitor_overtake: e.target.value })} className="px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white">
            <option value="">Any overtake state</option>
            <option value="true">Competitor overtook you</option>
            <option value="false">You still lead</option>
          </select>
          */}
          {/* Temporarily hidden — status filter dropdown is being redesigned. */}
          {/*
          <select data-testid="filter-status" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className="px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white">
            <option value="">Any status</option>
            <option value="active">Active</option>
            <option value="paused">Paused</option>
            <option value="delayed">Delayed</option>
            <option value="error">Error</option>
            <option value="no_result">No result</option>
          </select>
          */}
          <div className="flex gap-2">
            <input type="number" placeholder="Min vol" data-testid="filter-min-vol" className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm" value={filters.min_volume} onChange={(e) => setFilters({ ...filters, min_volume: e.target.value })} />
            <input type="number" placeholder="Min opp." data-testid="filter-min-opp" className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm" value={filters.min_opportunity} onChange={(e) => setFilters({ ...filters, min_opportunity: e.target.value })} />
            {activeFilterCount > 0 && (
              <button onClick={clearFilters} className="px-2 py-1 text-xs text-[#525B6A] hover:text-red-600 shrink-0"><X className="h-3.5 w-3.5" /></button>
            )}
          </div>
        </div>
      </div>

      {/* Focus modes */}
      <div className="mt-4 flex items-center gap-2 flex-wrap" data-testid="focus-modes">
        <FilterIcon className="h-3.5 w-3.5 text-[#8A94A6]" />
        <span className="text-[11px] uppercase tracking-wider text-[#8A94A6] font-medium">Focus</span>
        {[
          { v: "all", label: "All", tid: "all" },
          { v: "selected", label: `Selected (${selected.size})`, disabled: selected.size === 0, tid: "selected" },
          { v: "improved", label: "Improved", tid: "improved" },
          { v: "dropped", label: "Dropped", tid: "dropped" },
          { v: "top3", label: "Entered top 3", tid: "top3" },
          { v: "top10", label: "Entered top 10", tid: "top10" },
          { v: "lost-top10", label: "Lost top 10", tid: "lost-top10" },
          { v: "not-top100", label: "Not in top 100", tid: "not-top100" },
          { v: "paused", label: `Paused${usage?.keywords?.by_status?.paused ? ` (${usage.keywords.by_status.paused})` : ""}`, tid: "paused" },
          { v: "archived", label: `Archived${usage?.keywords?.by_status?.archived ? ` (${usage.keywords.by_status.archived})` : ""}`, tid: "archived" },
          // Temporarily hidden — these focus tabs are being redesigned:
          //   "Competitor overtook", "Hide unchanged", "Hide paused",
          //   "Active only", "Delayed / errors".
          // { v: "overtaken", label: "Competitor overtook", tid: "overtaken" },
          // { v: "no-unchanged", label: "Hide unchanged", tid: "unchanged" },
          // { v: "no-paused", label: "Hide paused", tid: "paused" },
          // { v: "active-only", label: "Active only", tid: "active" },
          // { v: "delayed", label: "Delayed / errors", tid: "delayed" },
        ].map((f) => (
          <button
            key={f.v}
            onClick={() => setFocus(f.v)}
            disabled={f.disabled}
            data-testid={`focus-${f.tid}`}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs border ${focus === f.v ? 'bg-[#0047FF] border-[#0047FF] text-white' : 'bg-white border-[#E2E8F0] text-[#525B6A] hover:border-[#0047FF] hover:text-[#0047FF]'} ${f.disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
          >
            {f.label}
          </button>
        ))}
        <div className="text-xs text-[#8A94A6] tabular-nums ml-auto flex items-center gap-3">
          <span>Showing {visibleKeywords.length} of {keywords.length}</span>
          <div className="relative">
            {(() => {
              const COLS = [
                ["country", "Country"], ["language", "Language"], ["device", "Device"], ["rank", "Daily rank"], ["prev", "Prev"],
                ["change", "Change"], ["volume", "Volume"], ["opp", "Opp."], ["ranking_url", "Target · Ranking URL"],
                ["trend", "Trend (30d)"], ["updated", "Updated"],
              ];
              const shownCols = COLS.filter(([k]) => cols[k] !== false).length;
              return (
                <>
                  <button data-testid="column-toggle" onClick={() => setShowColMenu((s) => !s)} className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-[#E2E8F0] hover:border-[#0047FF] hover:text-[#0047FF]">
                    <Columns3 className="h-3 w-3" /> Columns <span className="tabular-nums text-[#0A0D14] font-medium" data-testid="column-count">{shownCols}/{COLS.length}</span>
                  </button>
                  {showColMenu && (
                    <div className="absolute right-0 top-full mt-1 z-30 w-52 bg-white border border-[#E2E8F0] rounded-md shadow-xl p-2 text-left" data-testid="column-menu">
                      <div className="text-[10px] uppercase tracking-wider text-[#8A94A6] font-medium px-2 pb-1">Toggle columns ({shownCols}/{COLS.length})</div>
                      {COLS.map(([k, label]) => (
                        <label key={k} className="flex items-center gap-2 px-2 py-1 hover:bg-[#F8F9FA] rounded cursor-pointer">
                          <input type="checkbox" data-testid={`col-${k}`} checked={cols[k] !== false} onChange={(e) => setCols({ ...cols, [k]: e.target.checked })} className="accent-[#0047FF]" />
                          <span className="text-xs text-[#0A0D14] normal-case">{label}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      </div>

      {/* Selection summary */}
      {selectionStats && (
        <div className="mt-3 card-crisp p-3 flex items-center gap-6 flex-wrap text-sm" data-testid="selection-summary">
          <div className="flex items-center gap-2">
            <CheckSquare className="h-4 w-4 text-[#0047FF]" />
            <span className="font-medium text-[#0A0D14] tabular-nums">{selectionStats.count} selected</span>
          </div>
          <div className="text-[#525B6A]"><span className="text-[10px] uppercase tracking-wider text-[#8A94A6] mr-1">Volume</span><span className="tabular-nums font-medium text-[#0A0D14]">{selectionStats.volume.toLocaleString()}</span></div>
        </div>
      )}

      {/* Table */}
      <div className="mt-6 card-crisp overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F8F9FA] border-b border-[#E2E8F0]">
              <tr className="text-left text-[10px] uppercase tracking-wider text-[#8A94A6] font-medium">
                <th className="pl-4 pr-2 py-3 w-8">
                  <button data-testid="select-all-keywords" onClick={selectAllVisible} className="inline-flex cursor-pointer" title={allVisibleSelected ? "Deselect all visible" : "Select all visible"}>
                    {allVisibleSelected ? <CheckSquare className="h-4 w-4 text-[#0047FF]" /> : <Square className={`h-4 w-4 ${someVisibleSelected ? 'text-[#0047FF]' : 'text-[#8A94A6]'} hover:text-[#0047FF]`} />}
                  </button>
                </th>
                <th className="px-3 py-3">Keyword</th>
                {/* Status column temporarily hidden — restore by uncommenting
                    the <th> here and the matching <td> below in the row. */}
                {/* <th className="px-3 py-3">Status</th> */}
                {cols.country && <th className="px-3 py-3">Country</th>}
                {cols.language && <th className="px-3 py-3">Language</th>}
                {cols.device && <th className="px-3 py-3">Device</th>}
                {cols.rank && <SortableHead onClick={() => toggleSort("current_rank")} label="Daily rank" active={sort.field === "current_rank"} order={sort.order} />}
                {cols.prev && <SortableHead onClick={() => toggleSort("previous_rank")} label="Prev" active={sort.field === "previous_rank"} order={sort.order} />}
                {cols.change && <th className="px-3 py-3">Change</th>}
                {cols.volume && <SortableHead onClick={() => toggleSort("search_volume")} label="Volume" active={sort.field === "search_volume"} order={sort.order} />}
                {cols.opp && <th className="px-3 py-3">Opp.</th>}
                {cols.ranking_url && <th className="px-3 py-3">Target · Ranking URL</th>}
                {cols.trend && <th className="px-3 py-3">Trend (30d)</th>}
                {cols.updated && <th className="px-3 py-3">Updated</th>}
                <th className="px-2 py-3 w-8"></th>
              </tr>
            </thead>
            <tbody>
              {visibleKeywords.map((k) => (
                <KeywordRow key={k.id} k={k} cols={cols} countryList={countryList} languageList={languageList} onOpen={() => openKeyword(k)} selected={selected.has(k.id)} onToggleSelect={() => toggleSelect(k.id)} openMenu={openMenuId === k.id} menuCoords={openMenuId === k.id ? openMenuCoords : null} onOpenMenu={(coords) => { setOpenMenuCoords(coords || null); setOpenMenuId(openMenuId === k.id ? null : k.id); }} onCloseMenu={() => { setOpenMenuId(null); setOpenMenuCoords(null); }} onSingleAction={(action, extra) => runBulk(action, extra, [k.id])} trialCapIso={trialCapIso} />
              ))}
              {visibleKeywords.length === 0 && (
                <tr><td colSpan="15" className="text-center py-16">
                  {keywords.length === 0 ? (
                    <div className="space-y-3">
                      <div className="text-[#8A94A6] text-sm">No keywords in this project yet.</div>
                      <div className="flex items-center justify-center gap-2 flex-wrap">
                        <button onClick={() => setShowAdd(true)} data-testid="empty-add" className="btn-primary px-3 py-1.5 rounded-md text-sm inline-flex items-center gap-1"><Plus className="h-3.5 w-3.5" /> Add keywords</button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="text-[#8A94A6] text-sm">No keywords match this view.</div>
                      <div className="flex items-center justify-center gap-2 flex-wrap">
                        {activeFilterCount > 0 && <button onClick={clearFilters} data-testid="empty-clear-filters" className="btn-ghost px-3 py-1.5 rounded-md text-sm">Clear filters</button>}
                        {focus !== "all" && <button onClick={() => setFocus("all")} data-testid="empty-reset-focus" className="btn-ghost px-3 py-1.5 rounded-md text-sm">Reset focus</button>}
                      </div>
                    </div>
                  )}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      </>
      )}

      {/* Add keywords drawer */}
      {showAdd && <AddKeywordsDrawer projectId={id} project={project} onClose={() => setShowAdd(false)} onDone={() => { setShowAdd(false); loadKeywords(); loadProject(); }} />}
      {showImport && <ImportKeywordsModal projectId={id} project={project} onClose={() => setShowImport(false)} onDone={() => { setShowImport(false); loadKeywords(); loadProject(); }} />}

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex items-center gap-2 bg-[#0A0D14] text-white rounded-lg shadow-2xl px-4 py-3 max-w-[95vw] overflow-x-auto whitespace-nowrap" data-testid="bulk-bar">
          <span className="text-xs font-medium mr-2 whitespace-nowrap">{selected.size} selected</span>
          <button onClick={() => { const tag = window.prompt("Tag name"); if (tag) runBulk("add_tag", { tag: tag.trim() }); }} data-testid="bulk-add-tag" className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded bg-white/10 hover:bg-white/20 whitespace-nowrap"><Tag className="h-3.5 w-3.5" /> Add tag</button>
          <button onClick={() => { const tag = window.prompt("Tag to remove"); if (tag) runBulk("remove_tag", { tag: tag.trim() }); }} data-testid="bulk-remove-tag" className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded bg-white/10 hover:bg-white/20 whitespace-nowrap"><Tag className="h-3.5 w-3.5" /> Remove tag</button>
          <button onClick={() => runBulk("refresh")} data-testid="bulk-refresh" className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded bg-white/10 hover:bg-white/20 whitespace-nowrap"><RefreshCw className="h-3.5 w-3.5" /> Refresh</button>
          <div className="w-px h-5 bg-white/20 mx-1 shrink-0" />
          <button onClick={() => runBulk("pause")} data-testid="bulk-pause" className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded bg-white/10 hover:bg-white/20 whitespace-nowrap" title="Pause — stop scheduled checks but keep history"><Pause className="h-3.5 w-3.5" /> Pause</button>
          <button onClick={() => runBulk("resume")} data-testid="bulk-resume" className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded bg-white/10 hover:bg-white/20 whitespace-nowrap" title="Resume — re-enable scheduled checks"><Play className="h-3.5 w-3.5" /> Resume</button>
          <button onClick={() => runBulk("archive")} data-testid="bulk-archive" className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded bg-white/10 hover:bg-white/20 whitespace-nowrap" title="Archive — hide from active view, keep in database"><Archive className="h-3.5 w-3.5" /> Archive</button>
          <button onClick={() => runBulk("restore")} data-testid="bulk-restore" className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded bg-white/10 hover:bg-white/20 whitespace-nowrap" title="Restore — move archived keywords back to active"><ArchiveRestore className="h-3.5 w-3.5" /> Restore</button>
          <div className="w-px h-5 bg-white/20 mx-1 shrink-0" />
          <button onClick={exportSelectedCsv} data-testid="bulk-export" className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded bg-white/10 hover:bg-white/20 whitespace-nowrap"><Download className="h-3.5 w-3.5" /> Export</button>
          <button onClick={shareSelected} data-testid="bulk-share" className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded bg-white/10 hover:bg-white/20 whitespace-nowrap"><Share2 className="h-3.5 w-3.5" /> Share</button>
          {/* Add to report — temporarily hidden per product request */}
          {/* <button onClick={addSelectedToReport} data-testid="bulk-report" className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded bg-white/10 hover:bg-white/20 whitespace-nowrap"><FileText className="h-3.5 w-3.5" /> Add to report</button> */}
          <button onClick={() => { if (window.confirm(`Delete ${selected.size} keyword${selected.size === 1 ? '' : 's'}? This cannot be undone.`)) runBulk("delete"); }} data-testid="bulk-delete" className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded bg-red-500/20 hover:bg-red-500/40 text-red-200 whitespace-nowrap"><Trash2 className="h-3.5 w-3.5" /> Delete</button>
          <div className="w-px h-5 bg-white/20 mx-1 shrink-0" />
          <button onClick={clearSelection} data-testid="bulk-clear" className="text-xs px-2.5 py-1.5 rounded bg-white/5 hover:bg-white/10 whitespace-nowrap">Clear</button>
        </div>
      )}

      {/* Competitors panel */}
      {showComp && <CompetitorsDrawer projectId={id} competitors={competitors} onClose={() => setShowComp(false)} onChange={() => { loadCompetitors(); loadKeywords(); }} />}
      {showProReport && <ProReportModal project={project} onClose={() => setShowProReport(false)} />}
      {showExport && <ExportModal projectId={id} projectDomain={project?.domain} onClose={() => setShowExport(false)} />}

      {/* Keyword drawer */}
      {drawer && (
        <KeywordDrawer
          keyword={drawer}
          analysis={analysis}
          comparison={comparison}
          aiBusy={aiBusy}
          onRunAi={runAi}
          onClose={() => { setDrawer(null); setAnalysis(null); setComparison(null); }}
        />
      )}
    </div>
  );
}

function RankChartTooltip({ active, payload, label }) {
  if (!active || !payload || payload.length === 0) return null;
  // All series share the same payload row — pick the first with a payload obj.
  const row = payload.find((p) => p && p.payload)?.payload;
  if (!row) return null;
  const activities = row.activities || [];
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-md shadow-xl p-3 text-xs max-w-xs" data-testid="rank-chart-tooltip">
      <div className="font-medium text-[#0A0D14] mb-1">
        {label ? new Date(label).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : ""}
      </div>
      {row.rank != null && (
        <div className="text-[#525B6A]">
          Rank: <span className="font-medium text-[#0A0D14] tabular-nums">
            {row.inTop100 === false ? "Not in top 100" : `#${row.actualRank ?? row.rank}`}
          </span>
        </div>
      )}
      {activities.length > 0 && (
        <div className="mt-2 pt-2 border-t border-[#E2E8F0] space-y-2" data-testid="tooltip-activities">
          <div className="text-[10px] uppercase tracking-wider text-[#8A94A6]">
            {activities.length === 1 ? "SEO activity" : `${activities.length} SEO activities`}
          </div>
          {activities.map((a) => (
            <div key={a.id || `${a.activity_type}-${a.happened_at}-${a.title}`} className="flex items-start gap-2">
              <span className="mt-1 h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: colorFor(a.activity_type) }} />
              <div className="min-w-0">
                <div className="text-[#0A0D14] font-medium break-words">{a.title}</div>
                {a.note && (
                  <div className="text-[#525B6A] mt-0.5 break-words">
                    {a.note.length > 140 ? `${a.note.slice(0, 140)}…` : a.note}
                  </div>
                )}
                <div className="text-[10px] text-[#8A94A6] uppercase tracking-wider mt-0.5">
                  {categoryLabelFor(a.activity_type)} · {a.activity_type.replace(/_/g, " ")}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SortableHead({ onClick, label, active, order }) {
  return (
    <th className="px-3 py-3 cursor-pointer select-none" onClick={onClick}>
      <span className={`inline-flex items-center gap-1 ${active ? 'text-[#0047FF]' : ''}`}>
        {label} {active && <ChevronDown className={`h-3 w-3 ${order === 1 ? '' : 'rotate-180'} transition-transform`} />}
      </span>
    </th>
  );
}

// Compact daily-rank grid. Shows EVERY tracked day plus today (in case
// today hasn't been checked yet) as fixed-width columns, newest date on
// the LEFT (descending). When history is longer than the visible width,
// the grid scrolls horizontally — the newest columns stay in view first
// so users always see today at a glance, and older days pan into view.
// - Ranked day → soft green cell with the plain rank number.
// - Checked-but-not-ranked → white cell with an em-dash.
// - Today with no rank yet → clickable refresh icon that triggers a
//   single-keyword refresh (no need to open the 3-dot menu).
const _DAILY_CELL_WIDTH = 40; // px — one column in the grid
const _DAILY_MIN_COLUMNS = 4;  // always render at least this many days

function _pad2(n) { return n < 10 ? `0${n}` : `${n}`; }

// Union of today + every date in the keyword's history, sorted DESCENDING.
// Guarantees today is included so we can render the refresh icon when it
// hasn't been tracked yet, and back-fills any gaps between history dates
// so the grid reads chronologically (no missing days in the middle).
//
// `capIso` (optional, YYYY-MM-DD): when the account's trial has ended, the
// caller passes the trial expiry date so no dates AFTER it are rendered —
// tracking is halted post-expiry and we shouldn't show blank strip cells
// for days that will never be checked.
function _buildDailyDays(history, capIso) {
  const now = new Date();
  const todayUtc = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  const realTodayIso = `${todayUtc.getUTCFullYear()}-${_pad2(todayUtc.getUTCMonth() + 1)}-${_pad2(todayUtc.getUTCDate())}`;
  // Anchor = min(real today, capIso). If the cap is in the past, the strip
  // ends there and refresh CTA is hidden for the real "today".
  const todayIso = capIso && capIso < realTodayIso ? capIso : realTodayIso;

  const hist = (history || [])
    .filter((h) => h && h.date)
    .filter((h) => !capIso || h.date <= capIso);
  const dates = new Set(hist.map((h) => h.date));
  dates.add(todayIso);

  // Fill gaps between the earliest tracked day and today so the timeline is
  // continuous (no missing calendar days in the middle of the strip).
  let earliestIso = todayIso;
  dates.forEach((iso) => { if (iso < earliestIso) earliestIso = iso; });
  const cursor = new Date(`${earliestIso}T00:00:00Z`);
  const end = new Date(`${todayIso}T00:00:00Z`);
  while (cursor <= end) {
    const iso = `${cursor.getUTCFullYear()}-${_pad2(cursor.getUTCMonth() + 1)}-${_pad2(cursor.getUTCDate())}`;
    dates.add(iso);
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }

  // Pad forward from today if we haven't hit the minimum column count yet
  // (new keywords with only today would otherwise be a single tiny cell).
  const sorted = Array.from(dates).sort().reverse(); // newest first
  while (sorted.length < _DAILY_MIN_COLUMNS) {
    const oldest = sorted[sorted.length - 1];
    const d = new Date(`${oldest}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() - 1);
    const iso = `${d.getUTCFullYear()}-${_pad2(d.getUTCMonth() + 1)}-${_pad2(d.getUTCDate())}`;
    sorted.push(iso);
  }

  return { days: sorted, todayIso };
}

function DailyRankStrip({ history, testId, onRefresh, refreshing, capDate }) {
  const byDate = useMemo(() => {
    const map = {};
    (history || []).forEach((h) => { if (h && h.date) map[h.date] = h; });
    return map;
  }, [history]);

  const { days, todayIso } = useMemo(() => _buildDailyDays(history, capDate), [history, capDate]);

  // Max width fits ~6 columns before horizontal scroll kicks in — keeps the
  // keyword row from ballooning past comfortable width while still letting
  // users pan into weeks of history when they need it.
  const maxWidth = _DAILY_CELL_WIDTH * 6;

  return (
    <div
      className="inline-block overflow-x-auto daily-rank-scroll"
      style={{ maxWidth, scrollbarWidth: "thin" }}
      data-testid={testId}
    >
      <div className="flex text-[10px] font-medium text-[#8A94A6] tabular-nums whitespace-nowrap">
        {days.map((iso) => (
          <div
            key={`h-${iso}`}
            className="text-center pb-1"
            style={{ width: _DAILY_CELL_WIDTH, minWidth: _DAILY_CELL_WIDTH }}
          >
            {iso.slice(8, 10)}/{iso.slice(5, 7)}
          </div>
        ))}
      </div>
      <div className="flex whitespace-nowrap">
        {days.map((iso) => {
          const entry = byDate[iso];
          const hasRank = entry && entry.rank != null;
          const isToday = iso === todayIso;
          // Distinguish "check ran and returned null" (dash — not in top 100)
          // from "check hasn't run yet" (refresh icon on today). A history
          // entry exists → the rank check completed on that day; missing
          // entry on today → we've never checked, so offer the refresh.
          const checkCompleted = !!entry;
          const cellBase = "h-8 flex items-center justify-center text-xs tabular-nums border";
          const cellStyle = { width: _DAILY_CELL_WIDTH, minWidth: _DAILY_CELL_WIDTH };
          if (hasRank) {
            return (
              <div
                key={iso}
                style={cellStyle}
                title={entry.url ? `#${entry.rank} — ${entry.url}` : `#${entry.rank}`}
                className={`${cellBase} bg-emerald-50 border-emerald-200 text-emerald-700 font-medium`}
              >
                {entry.rank}
              </div>
            );
          }
          // Today with no completed check → refresh CTA (or spinner when a
          // check is already in flight). Any other empty cell — historic
          // gap OR today's check finished with a null rank — falls through
          // to the dash so agencies can see "checked, not ranking" clearly.
          // When `capDate` is set the trial has ended and refresh is disabled
          // server-side too, so we hide the CTA entirely.
          if (isToday && !capDate && onRefresh && (refreshing || !checkCompleted)) {
            return (
              <button
                key={iso}
                type="button"
                style={cellStyle}
                onClick={(e) => { e.stopPropagation(); if (!refreshing) onRefresh(); }}
                disabled={refreshing}
                title={refreshing ? "Refreshing…" : "Refresh this keyword now"}
                data-testid={`${testId}-refresh`}
                className={`${cellBase} bg-white border-[#E2E8F0] text-[#8A94A6] hover:text-[#0047FF] hover:border-[#0047FF] transition-colors ${refreshing ? "opacity-50 cursor-wait" : "cursor-pointer"}`}
              >
                <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
              </button>
            );
          }
          return (
            <div
              key={iso}
              style={cellStyle}
              title={checkCompleted ? `${iso} — Not in top 100` : `${iso} — Not checked`}
              className={`${cellBase} bg-white border-[#E2E8F0] text-[#8A94A6]`}
            >
              &ndash;
            </div>
          );
        })}
      </div>
    </div>
  );
}


// Render the target URL (user's intended ranking page) alongside the
// ranking URL Google actually returned. When both are set and their
// hostname+pathname disagree (ignoring trailing slash, www, protocol,
// hash), show an amber "Mismatch" pill so agencies can quickly spot
// keywords whose winning page isn't the one they planned to rank.
function _normalizeUrlForMatch(u) {
  if (!u) return "";
  try {
    const parsed = new URL(u);
    const host = parsed.hostname.replace(/^www\./, "").toLowerCase();
    const path = parsed.pathname.replace(/\/+$/, "");
    return `${host}${path}`;
  } catch {
    return String(u).toLowerCase().replace(/\/+$/, "");
  }
}

function _displayUrl(u) {
  if (!u) return "";
  try {
    const parsed = new URL(u);
    const host = parsed.hostname.replace(/^www\./, "");
    return `${host}${parsed.pathname === "/" ? "" : parsed.pathname}`;
  } catch {
    return u;
  }
}

function UrlMatchCell({ targetUrl, rankingUrl, kwId }) {
  const hasTarget = !!targetUrl;
  const hasRanking = !!rankingUrl;
  const targetKey = _normalizeUrlForMatch(targetUrl);
  const rankingKey = _normalizeUrlForMatch(rankingUrl);
  const match = hasTarget && hasRanking && targetKey === rankingKey;
  const mismatch = hasTarget && hasRanking && targetKey !== rankingKey;

  if (!hasTarget && !hasRanking) {
    return <span className="text-xs text-[#8A94A6]">—</span>;
  }

  return (
    <div className="flex flex-col gap-1 min-w-0" data-testid={`kw-url-cell-${kwId}`}>
      {hasTarget && (
        <div className="flex items-center gap-1 min-w-0" title={`Target: ${targetUrl}`}>
          <Target className="h-3 w-3 shrink-0 text-[#8A94A6]" />
          <a
            href={targetUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="truncate text-[11px] text-[#525B6A] hover:text-[#0047FF] hover:underline"
            data-testid={`kw-target-url-${kwId}`}
          >
            {_displayUrl(targetUrl)}
          </a>
        </div>
      )}
      {hasRanking ? (
        <div className="flex items-center gap-1 min-w-0" title={`Ranking: ${rankingUrl}`}>
          {hasTarget && (match
            ? <Check className="h-3 w-3 shrink-0 text-emerald-600" data-testid={`kw-url-match-${kwId}`} />
            : <AlertTriangle className="h-3 w-3 shrink-0 text-amber-500" data-testid={`kw-url-mismatch-${kwId}`} />
          )}
          <a
            href={rankingUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className={`truncate text-xs hover:underline ${mismatch ? "text-amber-700" : "text-[#0047FF]"}`}
            data-testid={`kw-ranking-url-${kwId}`}
          >
            {_displayUrl(rankingUrl)}
          </a>
          <ExternalLink className="h-3 w-3 shrink-0 text-[#8A94A6]" />
        </div>
      ) : (
        hasTarget && (
          <div className="text-[10px] text-[#8A94A6] italic pl-4" data-testid={`kw-no-ranking-${kwId}`}>
            Not ranking yet
          </div>
        )
      )}
      {mismatch && (
        <div className="text-[10px] font-medium text-amber-700 uppercase tracking-wider pl-4" data-testid={`kw-mismatch-label-${kwId}`}>
          Different page ranking
        </div>
      )}
    </div>
  );
}




function KeywordRow({ k, cols, countryList, languageList, onOpen, selected, onToggleSelect, openMenu, menuCoords, onOpenMenu, onCloseMenu, onSingleAction, trialCapIso }) {
  const c = k.current_rank, p = k.previous_rank;
  let delta = null, dir = null;
  if (c === null && p !== null) { delta = "out"; dir = "down"; }
  else if (c !== null && p === null) { delta = "in"; dir = "up"; }
  else if (c !== null && p !== null) {
    delta = Math.abs(p - c);
    if (c < p) dir = "up";
    else if (c > p) dir = "down";
    else dir = "flat";
  }
  const historyData = (k.history || []).filter((h) => h.rank !== null).map((h) => ({ date: h.date, rank: h.rank }));
  const status = k.status || "active";
  const statusMeta = {
    active: { label: "Active", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    paused: { label: "Paused", cls: "bg-slate-100 text-slate-600 border-slate-200" },
    archived: { label: "Archived", cls: "bg-slate-200 text-slate-700 border-slate-300" },  // legacy state — no UI entrypoint anymore
    pending: { label: "Pending", cls: "bg-blue-50 text-blue-700 border-blue-200" },
    delayed: { label: "Delayed", cls: "bg-amber-50 text-amber-700 border-amber-200" },
    error: { label: "Error", cls: "bg-red-50 text-red-700 border-red-200" },
    no_result: { label: "No result", cls: "bg-slate-100 text-slate-500 border-slate-200" },
  }[status] || { label: status, cls: "bg-slate-100 text-slate-600 border-slate-200" };
  const updatedAt = k.last_updated_at ? new Date(k.last_updated_at) : null;
  const hoursAgo = updatedAt ? Math.floor((Date.now() - updatedAt.getTime()) / 3600000) : null;
  // Per PRD §Phase 29 the daily-check reset is calendar-day (account tz),
  // NOT a rolling 24h window. So the "next refresh" hint has to compare
  // UTC calendar dates (account tz defaults to UTC) — otherwise the UI
  // says "wait 6 more hours" even after midnight has already unlocked a
  // fresh check server-side, which was the reported bug.
  const todayISO = new Date().toISOString().slice(0, 10);
  const lastCheckedISO = updatedAt ? updatedAt.toISOString().slice(0, 10) : null;
  const checkedToday = lastCheckedISO !== null && lastCheckedISO === todayISO;
  const updatedLabel = hoursAgo === null ? "—" : hoursAgo < 1 ? "Just now" : hoursAgo < 24 ? `${hoursAgo}h ago` : `${Math.floor(hoursAgo / 24)}d ago`;
  const noteCount = k.note_count || 0;

  const menuItem = (label, icon, onClick, danger = false) => (
    <button
      onClick={(e) => { e.stopPropagation(); onCloseMenu(); onClick(); }}
      className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 hover:bg-[#F8F9FA] ${danger ? 'text-red-600' : 'text-[#0A0D14]'}`}
    >
      {icon} {label}
    </button>
  );

  return (
    <tr data-testid={`kw-row-${k.id}`} className={`border-b border-[#E2E8F0] hover:bg-[#F8F9FA] ${selected ? 'bg-[#0047FF]/[0.04]' : ''} ${status === 'paused' || status === 'archived' ? 'opacity-60' : ''}`}>
      <td className="pl-4 pr-2 py-3 w-8" onClick={(e) => { e.stopPropagation(); onToggleSelect(); }}>
        <button data-testid={`row-checkbox-${k.id}`} className="inline-flex cursor-pointer">
          {selected ? <CheckSquare className="h-4 w-4 text-[#0047FF]" /> : <Square className="h-4 w-4 text-[#8A94A6] hover:text-[#0047FF]" />}
        </button>
      </td>
      <td onClick={onOpen} className="px-3 py-3 text-[#0A0D14] font-medium cursor-pointer">
        <div className="flex items-center gap-2">
          <span>{k.keyword}</span>
          {noteCount > 0 && (
            <span data-testid={`note-badge-${k.id}`} title={`${noteCount} note${noteCount === 1 ? '' : 's'}`} className="inline-flex items-center gap-0.5 text-[10px] text-[#0047FF] bg-[#0047FF]/[0.08] px-1.5 py-0.5 rounded">
              <StickyNote className="h-3 w-3" /> {noteCount}
            </span>
          )}
        </div>
        {k.tags?.length > 0 && <div className="mt-1 flex gap-1 flex-wrap">{k.tags.map((t) => <span key={t} className="pill text-[10px] py-0.5 px-1.5">{t}</span>)}</div>}
      </td>
      {/* Status cell temporarily hidden — restore by uncommenting alongside
          the header <th> above. */}
      {/*
      <td onClick={onOpen} className="px-3 py-3 cursor-pointer">
        <span data-testid={`kw-status-${k.id}`} className={`inline-flex items-center text-[10px] px-1.5 py-0.5 rounded border font-medium uppercase tracking-wider ${statusMeta.cls}`}>
          {statusMeta.label}
        </span>
      </td>
      */}
      {cols.country && <td onClick={onOpen} className="px-3 py-3 text-[#525B6A] cursor-pointer" title={countryName(k.country, countryList)}><span className="font-mono text-[11px] uppercase">{k.country}</span></td>}
      {cols.language && <td onClick={onOpen} className="px-3 py-3 text-[#525B6A] cursor-pointer" title={languageName(k.language, languageList)}><span className="font-mono text-[11px]">{k.language}</span></td>}
      {cols.device && <td onClick={onOpen} className="px-3 py-3 text-[#525B6A] capitalize cursor-pointer">{k.device}</td>}
      {cols.rank && (
        <td onClick={onOpen} className="px-3 py-3 cursor-pointer">
          {k.pending_rank_task_id ? (
            <span className="inline-flex items-center gap-1 text-[10px] font-medium text-[#0047FF] bg-blue-50 border border-blue-200 rounded px-1.5 py-0.5 uppercase tracking-wider" data-testid={`kw-checking-${k.id}`}>
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#0047FF] animate-pulse" />Checking
            </span>
          ) : (
            <DailyRankStrip
              history={k.history}
              testId={`kw-daily-strip-${k.id}`}
              onRefresh={() => onSingleAction("refresh")}
              refreshing={!!k.pending_rank_task_id}
              capDate={trialCapIso}
            />
          )}
        </td>
      )}
      {cols.prev && <td onClick={onOpen} className="px-3 py-3 tabular-nums text-[#8A94A6] cursor-pointer">{p ? `#${p}` : "—"}</td>}
      {cols.change && (
        <td onClick={onOpen} className="px-3 py-3 cursor-pointer">
          {dir === "up" && <span className="inline-flex items-center gap-0.5 text-emerald-600 tabular-nums font-medium"><ArrowUpRight className="h-3.5 w-3.5" />{delta === "in" ? "New" : delta}</span>}
          {dir === "down" && <span className="inline-flex items-center gap-0.5 text-red-600 tabular-nums font-medium"><ArrowDownRight className="h-3.5 w-3.5" />{delta === "out" ? "Out" : delta}</span>}
          {dir === "flat" && <span className="inline-flex items-center gap-0.5 text-[#8A94A6] tabular-nums"><Minus className="h-3.5 w-3.5" />0</span>}
        </td>
      )}
      {cols.volume && (
        <td onClick={onOpen} className="px-3 py-3 tabular-nums text-[#525B6A] cursor-pointer">
          {k.search_volume ? (
            <span>
              {k.search_volume.toLocaleString()}
              {k.volume_source === "manual" && <span className="ml-1 text-[9px] text-[#8A94A6] uppercase">m</span>}
              {k.volume_source === "api" && <span className="ml-1 text-[9px] text-[#0047FF] uppercase">est</span>}
            </span>
          ) : "—"}
        </td>
      )}
      {cols.opp && (
        <td onClick={onOpen} className="px-3 py-3 cursor-pointer">
          {typeof k.opportunity_score === 'number' && (
            <div className="inline-flex items-center gap-1.5">
              <div className="w-10 h-1.5 bg-[#E2E8F0] rounded-full overflow-hidden">
                <div className={k.opportunity_score >= 70 ? 'bg-emerald-500 h-full' : k.opportunity_score >= 40 ? 'bg-[#0047FF] h-full' : 'bg-slate-400 h-full'} style={{ width: `${k.opportunity_score}%` }} />
              </div>
              <span className="tabular-nums text-xs font-medium text-[#0A0D14] w-6 text-right">{k.opportunity_score}</span>
            </div>
          )}
        </td>
      )}
      {cols.ranking_url && (
        <td data-testid={`kw-url-${k.id}`} className="px-3 py-3 max-w-[280px] align-top">
          <UrlMatchCell targetUrl={k.target_url} rankingUrl={k.landing_url} kwId={k.id} />
        </td>
      )}
      {cols.trend && (
        <td onClick={onOpen} className="px-3 py-3 w-32 cursor-pointer">
          {historyData.length > 1 && (
            <div style={{ height: 24 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={historyData}>
                  <YAxis reversed hide domain={['dataMin', 'dataMax']} />
                  <Line type="monotone" dataKey="rank" stroke="#0047FF" dot={false} strokeWidth={1.5} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </td>
      )}
      {cols.updated && (
        <td onClick={onOpen} className="px-3 py-3 text-xs text-[#8A94A6] whitespace-nowrap cursor-pointer" title={updatedAt ? `Last checked: ${updatedAt.toLocaleString()}${checkedToday ? "\nAlready checked today. Next available tomorrow." : "\nAvailable to refresh now."}` : ""}>
          {updatedLabel}
          {checkedToday && status !== "paused" && status !== "archived" && (
            <div className="text-[10px] text-[#8A94A6]" data-testid={`kw-next-tomorrow-${k.id}`}>Next tomorrow</div>
          )}
        </td>
      )}
      <td className="px-2 py-3 w-8 relative">
        <button
          data-testid={`row-menu-${k.id}`}
          onClick={(e) => {
            e.stopPropagation();
            // Capture the button's viewport rect so we can position the menu
            // via `fixed` — the ancestor `<div className="overflow-x-auto">`
            // clips any `absolute` menu, which is why the popover looked
            // invisible on tap.
            const r = e.currentTarget.getBoundingClientRect();
            const vw = typeof window !== "undefined" ? window.innerWidth : r.right;
            const vh = typeof window !== "undefined" ? window.innerHeight : r.bottom;
            const menuWidth = 192; // Tailwind w-48 = 12rem = 192px
            const menuHeight = 260; // ~6 items + separator; slight overshoot is fine
            // Prefer right-aligned to the button; flip up if there's no
            // room below.
            const left = Math.max(8, Math.min(vw - menuWidth - 8, r.right - menuWidth));
            const top = r.bottom + menuHeight + 8 <= vh ? r.bottom + 4 : Math.max(8, r.top - menuHeight - 4);
            onOpenMenu({ top, left });
          }}
          className="p-1 text-[#8A94A6] hover:text-[#0047FF] hover:bg-[#F8F9FA] rounded"
        >
          <MoreVertical className="h-4 w-4" />
        </button>
        {openMenu && (
          <>
            <div className="fixed inset-0 z-[60]" onClick={onCloseMenu} />
            <div
              className="fixed z-[70] w-48 bg-white border border-[#E2E8F0] rounded-md shadow-xl py-1"
              style={{ top: menuCoords?.top ?? 0, left: menuCoords?.left ?? 0 }}
              data-testid={`row-menu-panel-${k.id}`}
            >
              {menuItem("View chart", <Sparkles className="h-3.5 w-3.5 text-[#0047FF]" />, onOpen)}
              {menuItem(`Notes (${noteCount})`, <StickyNote className="h-3.5 w-3.5 text-[#0047FF]" />, () => onOpen())}
              {menuItem("Add tag", <Tag className="h-3.5 w-3.5" />, () => { const t = window.prompt("Tag name"); if (t) onSingleAction("add_tag", { tag: t.trim() }); })}
              {status !== "paused"
                ? menuItem("Pause tracking", <Pause className="h-3.5 w-3.5" />, () => onSingleAction("pause"))
                : menuItem("Resume tracking", <Play className="h-3.5 w-3.5" />, () => onSingleAction("resume"))
              }
              {menuItem("Refresh now", <RefreshCw className="h-3.5 w-3.5" />, () => onSingleAction("refresh"))}
              <div className="border-t border-[#E2E8F0] my-1" />
              {menuItem("Delete", <Trash2 className="h-3.5 w-3.5" />, () => { if (window.confirm(`Delete "${k.keyword}"? This cannot be undone.`)) onSingleAction("delete"); }, true)}
            </div>
          </>
        )}
      </td>
    </tr>
  );
}

function AddKeywordsDrawer({ projectId, project, onClose, onDone }) {
  const [text, setText] = useState("");
  const [country, setCountry] = useState(project?.default_country || "US");
  const [language, setLanguage] = useState(project?.default_language || "en");
  const [device, setDevice] = useState(project?.default_device || "desktop");
  const [engine, setEngine] = useState(project?.default_engine || "google");
  const [volume, setVolume] = useState("");
  const [tagsText, setTagsText] = useState("");
  const [targetUrl, setTargetUrl] = useState(project?.target_url || "");
  const [busy, setBusy] = useState(false);
  const [countryList, setCountryList] = useState({ items: [], common: [] });
  const [languageList, setLanguageList] = useState({ items: [], common: [] });
  useEffect(() => { loadCountries().then(setCountryList); loadLanguages().then(setLanguageList); }, []);

  const submit = async () => {
    const keywords = text.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
    if (keywords.length === 0) return toast.error("Enter at least one keyword.");
    setBusy(true);
    try {
      await api.post("/keywords/bulk", {
        project_id: projectId,
        keywords, country, language, device, engine,
        search_volume: volume ? parseInt(volume, 10) : null,
        tags: tagsText.split(",").map((t) => t.trim()).filter(Boolean),
        target_url: targetUrl.trim() || null,
      });
      toast.success(`Added ${keywords.length} keyword${keywords.length === 1 ? '' : 's'} — real ranks land in ~1 min.`);
      onDone();
    } catch (err) { toast.error(err?.response?.data?.detail || "Add failed."); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" onClick={onClose}>
      <div className="w-full max-w-md bg-white h-full overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-[#E2E8F0] flex items-center justify-between">
          <div className="text-lg font-display font-semibold text-[#0A0D14]">Add keywords</div>
          <button onClick={onClose}><X className="h-4 w-4" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="text-xs font-medium text-[#525B6A] mb-1 block">Paste keywords (one per line)</label>
            <textarea rows="10" data-testid="add-kw-textarea" placeholder="best rank tracker&#10;seo tools for agencies&#10;keyword rank tracker" className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm font-mono" value={text} onChange={(e) => setText(e.target.value)} />
            <div className="text-xs text-[#8A94A6] mt-1 tabular-nums">{text.split(/\r?\n/).filter((s) => s.trim()).length} keywords</div>
          </div>
          <div>
            <label className="text-xs font-medium text-[#525B6A] mb-1 block">Target URL (optional)</label>
            <input
              type="text"
              data-testid="add-kw-target-url"
              placeholder={project?.target_url || `https://${project?.domain || "example.com"}/pricing`}
              value={targetUrl}
              onChange={(e) => setTargetUrl(e.target.value)}
              className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm"
            />
            <div className="text-[11px] text-[#8A94A6] mt-1">Specific page you want these keywords to rank for. Leave blank to inherit the project's target URL.</div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-[#525B6A] mb-1 block">Country</label>
              <SearchableSelect items={countryList.items} common={countryList.common} value={country} onChange={setCountry} testid="add-kw-country" placeholder="Select country" />
            </div>
            <div>
              <label className="text-xs font-medium text-[#525B6A] mb-1 block">Language</label>
              <SearchableSelect items={languageList.items} common={languageList.common} value={language} onChange={setLanguage} testid="add-kw-language" placeholder="Select language" />
            </div>
            <div>
              <label className="text-xs font-medium text-[#525B6A] mb-1 block">Device</label>
              <select data-testid="add-kw-device" value={device} onChange={(e) => setDevice(e.target.value)} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white">
                <option>desktop</option><option>mobile</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-[#525B6A] mb-1 block">Search engine</label>
              <select data-testid="add-kw-engine" value={engine} onChange={(e) => setEngine(e.target.value)} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white">
                <option>google</option>
                <option>bing</option>
              </select>
            </div>
            <div className="col-span-2">
              <label className="text-xs font-medium text-[#525B6A] mb-1 block">Volume (optional)</label>
              <input type="number" data-testid="add-kw-volume" value={volume} onChange={(e) => setVolume(e.target.value)} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm" />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-[#525B6A] mb-1 block">Tags (comma-separated)</label>
            <input data-testid="add-kw-tags" placeholder="brand, priority" value={tagsText} onChange={(e) => setTagsText(e.target.value)} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm" />
          </div>
        </div>
        <div className="p-6 border-t border-[#E2E8F0] bg-[#F8F9FA]">
          <button onClick={submit} disabled={busy} data-testid="add-kw-submit" className="btn-primary w-full px-4 py-2.5 rounded-md text-sm">{busy ? "Adding…" : "Add & track"}</button>
        </div>
      </div>
    </div>
  );
}

function CompetitorsDrawer({ projectId, competitors, onClose, onChange }) {
  const [domain, setDomain] = useState("");
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (!domain.trim()) return toast.error("Enter a domain.");
    setBusy(true);
    try {
      await api.post("/competitors", { project_id: projectId, domain: domain.trim().toLowerCase(), label: label.trim() || domain.trim() });
      toast.success("Competitor added. Seeding rankings…");
      setDomain(""); setLabel("");
      onChange();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const del = async (id) => {
    if (!window.confirm("Remove this competitor?")) return;
    await api.delete(`/competitors/${id}`);
    toast.success("Removed.");
    onChange();
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" onClick={onClose}>
      <div className="w-full max-w-md bg-white h-full overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="competitors-drawer">
        <div className="p-6 border-b border-[#E2E8F0] flex items-center justify-between">
          <div>
            <div className="text-xs text-[#8A94A6] uppercase tracking-wider">Competitors</div>
            <div className="mt-1 text-lg font-display font-semibold text-[#0A0D14]">{competitors.length} of 3 tracked</div>
          </div>
          <button onClick={onClose}><X className="h-4 w-4" /></button>
        </div>
        <div className="p-6 space-y-3">
          {competitors.map((c) => (
            <div key={c.id} data-testid={`comp-${c.id}`} className="flex items-center justify-between border border-[#E2E8F0] rounded-md p-3">
              <div>
                <div className="text-sm text-[#0A0D14] font-medium">{c.label}</div>
                <div className="text-xs text-[#8A94A6]">{c.domain}</div>
              </div>
              <button onClick={() => del(c.id)} data-testid={`comp-del-${c.id}`} className="p-1.5 text-[#8A94A6] hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
          {competitors.length === 0 && (
            <div className="text-sm text-[#8A94A6] text-center py-6">No competitors yet. Add up to 3 below.</div>
          )}
        </div>
        {competitors.length < 3 && (
          <div className="p-6 border-t border-[#E2E8F0] bg-[#F8F9FA] space-y-3">
            <div>
              <label className="text-xs font-medium text-[#525B6A] mb-1 block">Domain</label>
              <input data-testid="comp-domain" placeholder="competitor.com" value={domain} onChange={(e) => setDomain(e.target.value)} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white" />
            </div>
            <div>
              <label className="text-xs font-medium text-[#525B6A] mb-1 block">Label (optional)</label>
              <input data-testid="comp-label" placeholder="Competitor Inc." value={label} onChange={(e) => setLabel(e.target.value)} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white" />
            </div>
            <button onClick={add} disabled={busy} data-testid="comp-add" className="btn-primary w-full px-4 py-2 rounded-md text-sm inline-flex items-center justify-center gap-2">
              <Users className="h-4 w-4" /> {busy ? "Adding…" : "Add competitor"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}


function KeywordDrawer({ keyword, analysis, comparison, aiBusy, onRunAi, onClose }) {
  // Include EVERY history point — even "not in top 100" checks — so the
  // line stays continuous. Null ranks get plotted at the OFF_RANK sentinel
  // (=101) and the y-axis reserves a slot for the "Not in top 100" label.
  const OFF_RANK = 101;
  const historyData = useMemo(
    () => (keyword.history || []).map((h) => ({
      date: h.date,
      // What we actually plot on the y-axis: real rank when present, 101 sentinel when out of top 100.
      rank: h.rank != null ? h.rank : OFF_RANK,
      // The real rank as returned by the API — used for tooltip + label rendering.
      actualRank: h.rank,
      inTop100: h.rank != null,
    })),
    [keyword]
  );
  // Fetch SEO activities scoped to this keyword so we can overlay markers on the chart.
  const [activities, setActivities] = useState([]);
  useEffect(() => {
    api.get("/activities", { params: { project_id: keyword.project_id, keyword_id: keyword.id } })
      .then((r) => setActivities(r.data || []))
      .catch(() => setActivities([]));
  }, [keyword.id, keyword.project_id]);

  // Merge history + activities into one dataset. For each activity, snap to the
  // closest history date so the marker sits on the rank line. Multiple activities
  // on the same date are grouped (shown together in the tooltip).
  const chartData = useMemo(() => {
    if (historyData.length === 0) return [];
    const sorted = [...historyData].sort((a, b) => (a.date < b.date ? -1 : 1));
    const byDate = new Map(sorted.map((h) => [
      h.date.slice(0, 10),
      { date: h.date, rank: h.rank, actualRank: h.actualRank, inTop100: h.inTop100, activities: [] },
    ]));
    const orderedDates = sorted.map((h) => h.date.slice(0, 10));
    const snap = (d) => {
      let best = orderedDates[0];
      for (const od of orderedDates) {
        if (od <= d) best = od; else break;
      }
      return best;
    };
    activities.forEach((a) => {
      const d = (a.occurred_at || "").slice(0, 10);
      if (!d) return;
      const snapped = byDate.has(d) ? d : snap(d);
      const row = byDate.get(snapped);
      if (row) row.activities.push(a);
    });
    return Array.from(byDate.values()).map((row) => {
      const out = { ...row };
      ["backlinks", "content", "technical"].forEach((c) => { out[`${c}_y`] = null; });
      row.activities.forEach((a) => {
        const c = categoryFor(a.activity_type);
        // Snap activity markers to the plotted rank (may be the OFF_RANK sentinel).
        out[`${c}_y`] = row.rank;
      });
      return out;
    });
  }, [historyData, activities]);

  // Format the label rendered directly above each rank dot: real ranks
  // show "#12", the out-of-top-100 sentinel shows "n/a" so the reader
  // instantly understands the point without hovering.
  const renderRankLabel = ({ x, y, value, index }) => {
    const point = chartData[index];
    if (!point) return null;
    const inTop = point.inTop100;
    const text = inTop ? `#${point.actualRank}` : "n/a";
    return (
      <text x={x} y={y - 8} textAnchor="middle" fill={inTop ? "#0047FF" : "#8A94A6"} fontSize={10} fontWeight={600}>
        {text}
      </text>
    );
  };

  const presentCategories = useMemo(() => {
    const set = new Set(activities.map((a) => categoryFor(a.activity_type)));
    return Array.from(set);
  }, [activities]);
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" onClick={onClose}>
      <div className="w-full max-w-2xl bg-white h-full overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="keyword-drawer">
        <div className="p-6 border-b border-[#E2E8F0] flex items-start justify-between">
          <div>
            <div className="text-xs text-[#8A94A6] uppercase tracking-wider">Keyword</div>
            <div className="mt-1 text-xl font-display font-semibold text-[#0A0D14]">{keyword.keyword}</div>
            <div className="mt-1 text-xs text-[#525B6A]">{keyword.country} · {keyword.device} · {keyword.engine}</div>
          </div>
          <button onClick={onClose}><X className="h-4 w-4" /></button>
        </div>
        <div className="p-6 grid grid-cols-3 gap-4">
          <div className="border border-[#E2E8F0] rounded-md p-3">
            <div className="text-[10px] uppercase tracking-wider text-[#8A94A6]">Current</div>
            <div className="stat-value text-2xl font-bold text-[#0047FF]">{keyword.current_rank ? `#${keyword.current_rank}` : "—"}</div>
          </div>
          <div className="border border-[#E2E8F0] rounded-md p-3">
            <div className="text-[10px] uppercase tracking-wider text-[#8A94A6]">Previous</div>
            <div className="stat-value text-2xl font-bold text-[#525B6A]">{keyword.previous_rank ? `#${keyword.previous_rank}` : "—"}</div>
          </div>
          <div className="border border-[#E2E8F0] rounded-md p-3">
            <div className="text-[10px] uppercase tracking-wider text-[#8A94A6]">Best</div>
            <div className="stat-value text-2xl font-bold text-emerald-600">{keyword.best_rank ? `#${keyword.best_rank}` : "—"}</div>
          </div>
        </div>

        <div className="px-6 pb-6">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs uppercase tracking-wider text-[#8A94A6] font-medium">Ranking history (30 days)</div>
            {presentCategories.length > 0 && (
              <div className="flex items-center gap-3 text-[10px] uppercase tracking-wider text-[#525B6A]" data-testid="chart-legend">
                {Object.entries(ACTIVITY_CATEGORIES).map(([key, meta]) => {
                  const active = presentCategories.includes(key);
                  return (
                    <span
                      key={key}
                      data-testid={`legend-${key}`}
                      className={`inline-flex items-center gap-1.5 ${active ? '' : 'opacity-40'}`}
                    >
                      <span className="h-2.5 w-2.5 rounded-full ring-2 ring-white" style={{ backgroundColor: meta.color }} />
                      {meta.label}
                    </span>
                  );
                })}
              </div>
            )}
          </div>
          <div className="card-crisp p-4" style={{ height: 260 }}>
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData} margin={{ top: 20, right: 16, bottom: 8, left: 8 }}>
                  <YAxis
                    reversed
                    domain={[1, 101]}
                    ticks={[1, 10, 25, 50, 75, 100, 101]}
                    tick={{ fontSize: 10, fill: '#8A94A6' }}
                    // Render the sentinel row as "n/a" so the axis reads
                    // as a proper "beyond top 100" label instead of "101".
                    tickFormatter={(v) => (v === 101 ? 'n/a' : String(v))}
                    width={38}
                    allowDataOverflow={false}
                  />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10, fill: '#8A94A6' }}
                    tickFormatter={(d) => new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                    minTickGap={30}
                  />
                  <RTooltip content={<RankChartTooltip />} cursor={{ stroke: '#E2E8F0' }} />
                  <Line
                    type="monotone"
                    dataKey="rank"
                    stroke="#0047FF"
                    dot={{ r: 3, fill: '#0047FF' }}
                    activeDot={{ r: 5 }}
                    strokeWidth={2}
                    isAnimationActive={false}
                    name="Rank"
                    connectNulls
                  >
                    <LabelList dataKey="rank" content={renderRankLabel} />
                  </Line>
                  {/* Activity markers — one hidden-line series per category, coloured dots only */}
                  {Object.entries(ACTIVITY_CATEGORIES).map(([key, meta]) => (
                    <Line
                      key={key}
                      type="monotone"
                      dataKey={`${key}_y`}
                      stroke="transparent"
                      isAnimationActive={false}
                      dot={{ r: 6, fill: meta.color, stroke: '#fff', strokeWidth: 2 }}
                      activeDot={{ r: 8, fill: meta.color, stroke: '#fff', strokeWidth: 2 }}
                      connectNulls={false}
                      legendType="none"
                      name={meta.label}
                    />
                  ))}
                </ComposedChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-sm text-[#8A94A6] flex items-center justify-center h-full">Not enough data yet.</div>
            )}
          </div>
          <div className="mt-2 text-[11px] text-[#8A94A6] italic" data-testid="rank-chart-legend-note">
            n/a = ranking is not in top 100
          </div>
        </div>

        {(keyword.target_url || keyword.landing_url) && (
          <div className="px-6 pb-6 space-y-3">
            {keyword.target_url && (
              <div>
                <div className="text-xs uppercase tracking-wider text-[#8A94A6] font-medium mb-1 flex items-center gap-1.5"><Target className="h-3 w-3" /> Target URL</div>
                <a href={keyword.target_url} target="_blank" rel="noreferrer" className="text-sm text-[#525B6A] hover:text-[#0047FF] hover:underline inline-flex items-center gap-1 break-all">{keyword.target_url} <ExternalLink className="h-3 w-3 shrink-0" /></a>
              </div>
            )}
            {keyword.landing_url && (
              <div>
                <div className="text-xs uppercase tracking-wider text-[#8A94A6] font-medium mb-1 flex items-center gap-1.5">
                  Ranking URL
                  {keyword.target_url && (
                    _normalizeUrlForMatch(keyword.target_url) === _normalizeUrlForMatch(keyword.landing_url)
                      ? <span className="inline-flex items-center gap-1 text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5"><Check className="h-3 w-3" /> Match</span>
                      : <span className="inline-flex items-center gap-1 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5"><AlertTriangle className="h-3 w-3" /> Different page</span>
                  )}
                </div>
                <a href={keyword.landing_url} target="_blank" rel="noreferrer" className="text-sm text-[#0047FF] inline-flex items-center gap-1 break-all">{keyword.landing_url} <ExternalLink className="h-3 w-3 shrink-0" /></a>
              </div>
            )}
          </div>
        )}

        {comparison && comparison.rows && comparison.rows.length > 1 && (
          <div className="px-6 pb-8">
            <div className="text-xs uppercase tracking-wider text-[#8A94A6] font-medium mb-3">Competitor comparison</div>
            <div className="card-crisp overflow-hidden" data-testid="comparison-panel">
              {comparison.overtaken_by?.length > 0 && (
                <div className="px-4 py-2.5 bg-amber-50 text-amber-800 text-xs border-b border-amber-100">
                  Overtaken by {comparison.overtaken_by.join(", ")}
                </div>
              )}
              {comparison.rows.map((r, i) => {
                const c = r.current_rank, p = r.previous_rank;
                let dir = null;
                if (c !== null && p !== null) dir = c < p ? 'up' : c > p ? 'down' : 'flat';
                return (
                  <div key={`${r.kind}-${r.domain || r.label}`} className={`grid grid-cols-12 items-center px-4 py-2.5 border-t border-[#E2E8F0] text-sm ${i === 0 ? 'bg-[#E0E7FF]/40' : ''}`}>
                    <div className="col-span-5">
                      <div className="text-[#0A0D14] font-medium">{r.label}</div>
                      <div className="text-xs text-[#8A94A6]">{r.domain}</div>
                    </div>
                    <div className="col-span-1 text-xs text-[#8A94A6] uppercase tracking-wider">{r.kind === 'you' ? 'You' : 'Comp'}</div>
                    <div className="col-span-3 tabular-nums font-medium text-[#0A0D14]">{c ? `#${c}` : '—'}</div>
                    <div className="col-span-3">
                      {dir === 'up' && <span className="inline-flex items-center gap-0.5 text-emerald-600 tabular-nums text-xs"><ArrowUpRight className="h-3 w-3" />{Math.abs(p - c)}</span>}
                      {dir === 'down' && <span className="inline-flex items-center gap-0.5 text-red-600 tabular-nums text-xs"><ArrowDownRight className="h-3 w-3" />{Math.abs(c - p)}</span>}
                      {dir === 'flat' && <span className="text-xs text-[#8A94A6]">unchanged</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="px-6 pb-8">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs uppercase tracking-wider text-[#8A94A6] font-medium">Keyword Timeline</div>
          </div>
          <KeywordTimeline keywordId={keyword.id} />
        </div>

        <KeywordNotesPanel keywordId={keyword.id} />

        {/* AI ranking analysis — temporarily hidden per product request */}
        {/*
        <div className="px-6 pb-8">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs uppercase tracking-wider text-[#8A94A6] font-medium">AI ranking analysis</div>
            <button onClick={onRunAi} disabled={aiBusy} data-testid="ai-run" className="btn-primary px-3 py-1.5 rounded-md text-xs inline-flex items-center gap-1">
              <Sparkles className="h-3 w-3" /> {aiBusy ? "Analyzing…" : (analysis ? "Re-analyze" : "Analyze")}
            </button>
          </div>
          {analysis ? (
            <div className="card-crisp p-5 space-y-4" data-testid="ai-result">
              <div>
                <div className="text-xs font-medium text-[#525B6A] uppercase tracking-wider">What changed</div>
                <div className="mt-1 text-[#0A0D14]">{analysis.what_changed}</div>
              </div>
              <div>
                <div className="text-xs font-medium text-[#525B6A] uppercase tracking-wider">Likely reasons</div>
                <ul className="mt-2 space-y-1.5">
                  {analysis.likely_reasons.map((r, i) => (
                    <li key={i} className="text-sm text-[#0A0D14] flex items-start gap-2"><span className="text-[#0047FF] tabular-nums">·</span> {r}</li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="text-xs font-medium text-[#525B6A] uppercase tracking-wider">Recommended actions</div>
                <ul className="mt-2 space-y-1.5">
                  {analysis.recommended_actions.map((r, i) => (
                    <li key={i} className="text-sm text-[#0A0D14] flex items-start gap-2"><TrendingUp className="h-3.5 w-3.5 text-emerald-600 mt-0.5 shrink-0" /> {r}</li>
                  ))}
                </ul>
              </div>
              {analysis.generated_at && <div className="text-[10px] text-[#8A94A6] uppercase tracking-wider">Generated {new Date(analysis.generated_at).toLocaleString()}</div>}
            </div>
          ) : (
            <div className="card-crisp p-5 text-sm text-[#8A94A6]">Click <b>Analyze</b> to get a plain-English explanation of this ranking change and recommended actions.</div>
          )}
        </div>
        */}
      </div>
    </div>
  );
}

function KeywordNotesPanel({ keywordId }) {
  const [notes, setNotes] = useState([]);
  const [text, setText] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/keywords/${keywordId}/notes`).then((r) => setNotes(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [keywordId]);

  const add = async () => {
    const t = text.trim();
    if (!t) return;
    setBusy(true);
    try {
      await api.post(`/keywords/${keywordId}/notes`, { text: t });
      setText("");
      load();
      toast.success("Note added.");
    } catch { toast.error("Failed to add note."); }
    finally { setBusy(false); }
  };
  const save = async (id) => {
    const t = editText.trim();
    if (!t) return;
    try {
      await api.patch(`/keywords/notes/${id}`, { text: t });
      setEditingId(null); setEditText("");
      load();
    } catch { toast.error("Failed to save."); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this note?")) return;
    try {
      await api.delete(`/keywords/notes/${id}`);
      load();
    } catch { toast.error("Failed to delete."); }
  };

  return (
    <div className="px-6 pb-8" data-testid="keyword-notes-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs uppercase tracking-wider text-[#8A94A6] font-medium">Notes</div>
        <span className="text-[10px] text-[#8A94A6] tabular-nums">{notes.length} note{notes.length === 1 ? '' : 's'}</span>
      </div>
      <div className="card-crisp p-4 space-y-3">
        <div className="flex items-start gap-2">
          <textarea
            data-testid="note-input"
            rows={2}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="e.g. Updated title tag on the landing page — expecting rank improvement in ~48h."
            className="flex-1 px-3 py-2 border border-[#E2E8F0] rounded-md text-sm"
          />
          <button data-testid="note-add" onClick={add} disabled={busy || !text.trim()} className="btn-primary px-3 py-2 rounded-md text-sm inline-flex items-center gap-1 shrink-0">
            <Plus className="h-3.5 w-3.5" /> Add
          </button>
        </div>
        {notes.length === 0 ? (
          <div className="text-xs text-[#8A94A6] py-2">No notes yet. Attach context so you (and your team) remember why rankings moved.</div>
        ) : (
          <div className="space-y-2">
            {notes.map((n) => (
              <div key={n.id} data-testid={`note-${n.id}`} className="border border-[#E2E8F0] rounded-md p-3">
                {editingId === n.id ? (
                  <div className="space-y-2">
                    <textarea rows={2} value={editText} onChange={(e) => setEditText(e.target.value)} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm" data-testid={`note-edit-input-${n.id}`} />
                    <div className="flex items-center gap-2">
                      <button onClick={() => save(n.id)} data-testid={`note-save-${n.id}`} className="btn-primary px-2.5 py-1 rounded text-xs">Save</button>
                      <button onClick={() => { setEditingId(null); setEditText(""); }} className="text-xs text-[#8A94A6]">Cancel</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="text-sm text-[#0A0D14] whitespace-pre-wrap">{n.text}</div>
                    <div className="mt-2 flex items-center justify-between text-[10px] text-[#8A94A6]">
                      <span>{n.author_name || n.author_email || "Team"} · {new Date(n.created_at).toLocaleString()}{n.updated_at ? ' · edited' : ''}</span>
                      <div className="flex items-center gap-3">
                        <button onClick={() => { setEditingId(n.id); setEditText(n.text); }} data-testid={`note-edit-${n.id}`} className="hover:text-[#0047FF]">Edit</button>
                        <button onClick={() => del(n.id)} data-testid={`note-delete-${n.id}`} className="hover:text-red-600">Delete</button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ImportKeywordsModal({ projectId, project, onClose, onDone }) {
  const { user } = useAuth();
  const isTrial = (user?.plan || "trial") === "trial";
  const [file, setFile] = useState(null);
  const [preset, setPreset] = useState("standard"); // standard | migration | competitor
  const [country, setCountry] = useState(project?.default_country || "US");
  const [language, setLanguage] = useState(project?.default_language || "en");
  const [device, setDevice] = useState(project?.default_device || "desktop");
  const [engine, setEngine] = useState(project?.default_engine || "google");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [countryList, setCountryList] = useState({ items: [], common: [] });
  const [languageList, setLanguageList] = useState({ items: [], common: [] });
  useEffect(() => { loadCountries().then(setCountryList); loadLanguages().then(setLanguageList); }, []);

  const downloadSample = async (kind = "csv") => {
    try {
      const r = await api.get(`/keywords/${kind}/sample`, { params: { type: preset }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `serpnex-import-${preset}.${kind}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download sample."); }
  };

  const submit = async () => {
    if (!file) return toast.error("Choose a CSV or XLSX file first.");
    setBusy(true);
    const fd = new FormData();
    fd.append("project_id", projectId);
    fd.append("country", country);
    fd.append("language", language);
    fd.append("device", device);
    fd.append("engine", engine);
    fd.append("file", file);
    try {
      const r = await api.post("/keywords/csv/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data);
      if (r.data.inserted > 0) toast.success(`Imported ${r.data.inserted} keyword${r.data.inserted === 1 ? '' : 's'}.`);
      if (!r.data.errors || r.data.errors.length === 0) onDone();
    } catch { toast.error("Import failed."); }
    finally { setBusy(false); }
  };

  const PRESETS = [
    {
      key: "standard",
      title: "Standard",
      subtitle: "Add fresh keywords",
      columns: "keyword, country, language, device, engine, tags, search_volume",
      note: "Everything except `keyword` uses the defaults below when left blank. SerpNex will start rank tracking from today.",
    },
    {
      key: "migration",
      title: "Migrate from another tool",
      subtitle: "Bring your rank history",
      columns: "keyword, country, language, device, engine, tags, year-month-date:position",
      note: "The final column carries your existing rank history as pipe-separated pairs — e.g. 2026-01-15:8|2026-01-22:6. Your rank chart continues right where you left off.",
      paid: true,
    },
  ];
  const active = PRESETS.find((p) => p.key === preset) || PRESETS[0];
  const migrationLocked = isTrial && active.paid;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="import-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg max-w-xl w-full shadow-2xl max-h-[92vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-[#E2E8F0] flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <div className="text-lg font-display font-semibold text-[#0A0D14]">Import keywords</div>
            <div className="text-xs text-[#8A94A6] mt-0.5">Pick a template, download it, fill it in, upload it.</div>
          </div>
          <button onClick={onClose} className="text-[#8A94A6] hover:text-[#0A0D14]"><X className="h-4 w-4" /></button>
        </div>

        <div className="px-6 py-4 space-y-4">
          {/* Preset picker */}
          <div className="grid grid-cols-2 gap-2" data-testid="import-preset-picker">
            {PRESETS.map((p) => {
              const locked = isTrial && p.paid;
              return (
                <button
                  key={p.key}
                  onClick={() => { setPreset(p.key); setResult(null); }}
                  data-testid={`import-preset-${p.key}`}
                  className={`relative text-left p-3 rounded-md border transition ${preset === p.key ? 'border-[#0047FF] bg-[#0047FF]/[0.04]' : 'border-[#E2E8F0] hover:border-[#8A94A6]'}`}
                >
                  <div className="flex items-center gap-1.5">
                    <div className={`text-[11px] font-semibold ${preset === p.key ? 'text-[#0047FF]' : 'text-[#0A0D14]'}`}>{p.title}</div>
                    {locked && <Lock className="h-2.5 w-2.5 text-amber-600" />}
                  </div>
                  <div className="text-[10px] text-[#525B6A] mt-0.5 leading-tight">{p.subtitle}</div>
                  {locked && (
                    <div className="mt-1 inline-flex items-center gap-1 text-[9px] uppercase tracking-wider text-amber-700 font-semibold" data-testid={`import-preset-${p.key}-badge`}>
                      Paid plans only
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {/* Trial-user upsell when the migration preset is selected */}
          {migrationLocked && (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-3" data-testid="import-migration-upsell">
              <Lock className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
              <div className="flex-1 text-xs text-[#0A0D14] leading-relaxed">
                <div className="font-semibold">Rank-history import is a paid feature.</div>
                <div className="mt-1 text-[#525B6A]">
                  Your 7-day trial can add fresh keywords, but bringing history from Ahrefs / Semrush / another tracker unlocks with any paid plan starting at $4.99/month. Your projects and existing data stay put — you're just unlocking this one workflow.
                </div>
                <Link to="/app/settings" onClick={onClose} data-testid="import-migration-upgrade" className="mt-2 inline-flex btn-primary px-3 py-1.5 rounded-md text-[11px] font-semibold items-center gap-1.5">Upgrade to unlock</Link>
              </div>
            </div>
          )}

          {/* Active preset explainer + download buttons */}
          <div className="rounded-md border border-[#E2E8F0] bg-[#F8F9FA] px-4 py-3 flex items-start justify-between gap-3">
            <div className="text-xs text-[#525B6A] min-w-0">
              <div className="font-medium text-[#0A0D14] mb-1">Expected columns</div>
              <div className="font-mono text-[11px] text-[#525B6A] break-all">{active.columns}</div>
              <div className="mt-1.5 text-[#525B6A] leading-relaxed">{active.note}</div>
            </div>
            <div className="shrink-0 flex flex-col gap-1.5">
              <button onClick={() => downloadSample("csv")} data-testid="import-sample-csv" className="btn-ghost px-2.5 py-1.5 rounded-md text-xs inline-flex items-center gap-1"><Download className="h-3 w-3" /> CSV sample</button>
              <button onClick={() => downloadSample("xlsx")} data-testid="import-sample-xlsx" className="btn-ghost px-2.5 py-1.5 rounded-md text-xs inline-flex items-center gap-1"><Download className="h-3 w-3" /> XLSX sample</button>
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-[#525B6A] mb-1 block">CSV or XLSX file</label>
            <input type="file" accept=".csv,.xlsx" data-testid="import-file" disabled={migrationLocked} onChange={(e) => { setFile(e.target.files[0] || null); setResult(null); }} className="w-full text-sm file:mr-3 file:px-3 file:py-1.5 file:rounded file:border-0 file:bg-[#0047FF]/10 file:text-[#0047FF] file:text-xs file:font-medium hover:file:bg-[#0047FF]/20 disabled:opacity-50" />
            {file && <div className="mt-1 text-[11px] text-[#8A94A6]">{file.name} · {(file.size / 1024).toFixed(1)} KB</div>}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-[#525B6A] mb-1 block">Default country</label>
              <SearchableSelect items={countryList.items} common={countryList.common} value={country} onChange={setCountry} testid="import-country" placeholder="Country" />
            </div>
            <div>
              <label className="text-xs font-medium text-[#525B6A] mb-1 block">Default language</label>
              <SearchableSelect items={languageList.items} common={languageList.common} value={language} onChange={setLanguage} testid="import-language" placeholder="Language" />
            </div>
            <div>
              <label className="text-xs font-medium text-[#525B6A] mb-1 block">Default device</label>
              <select data-testid="import-device" value={device} onChange={(e) => setDevice(e.target.value)} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white">
                <option>desktop</option><option>mobile</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-[#525B6A] mb-1 block">Default search engine</label>
              <select data-testid="import-engine" value={engine} onChange={(e) => setEngine(e.target.value)} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white">
                <option>google</option><option>bing</option>
              </select>
            </div>
          </div>

          {result && (
            <div className="rounded-md border border-[#E2E8F0] p-3 text-xs" data-testid="import-result">
              <div className="text-[#0A0D14] font-medium">Imported {result.inserted} keyword{result.inserted === 1 ? '' : 's'}. {result.skipped > 0 && <span className="text-amber-600">Skipped {result.skipped}.</span>}</div>
              {result.errors?.length > 0 && (
                <div className="mt-2 max-h-32 overflow-y-auto space-y-1">
                  {result.errors.map((er) => (
                    <div key={`${er.line}-${er.field}-${er.value}`} className="text-[11px] text-red-600">Line {er.line} · {er.keyword || '—'} — <span className="text-[#525B6A]">{er.field}={er.value}</span> · {er.message}</div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-[#E2E8F0] flex items-center justify-end gap-2 sticky bottom-0 bg-white">
          <button onClick={onClose} className="btn-ghost px-3 py-2 rounded-md text-sm">Close</button>
          <button onClick={submit} disabled={busy || !file || migrationLocked} data-testid="import-submit" className="btn-primary px-4 py-2 rounded-md text-sm disabled:opacity-50">{busy ? "Importing…" : "Import file"}</button>
        </div>
      </div>
    </div>
  );
}
